<template>
  <div v-if="canUse" class="pex">
    <span class="pex__title">Gastos de esta nave</span>

    <div v-if="summary" class="pex__summary">
      <span class="pex__total">{{ formatCurrency(summary.total12m) }}</span>
      <span class="pex__sub">últimos 12 meses · {{ summary.count }} gasto{{ summary.count === 1 ? '' : 's' }}</span>
    </div>
    <ul v-if="summary?.recent?.length" class="pex__list">
      <li v-for="e in summary.recent" :key="e.id">
        <span class="pex__name">{{ e.name }}</span>
        <span class="pex__date">{{ formatDate(e.date) }}</span>
        <span class="pex__amount">{{ formatCurrency(e.amount) }}</span>
        <button type="button" class="pex__del" :class="{ 'pex__del--confirm': confirmId === e.id }" :disabled="deletingId === e.id" :title="confirmId === e.id ? 'Clic otra vez para eliminar' : 'Eliminar gasto'" @click="removeExpense(e.id)">{{ confirmId === e.id ? '¿Eliminar?' : '✕' }}</button>
      </li>
    </ul>

    <button v-if="!adding" type="button" class="pex__add" @click="startAdding">＋ Registrar gasto de esta nave</button>

    <form v-else class="pex__form" @submit.prevent="save">
      <select v-model="form.category" class="pex__input">
        <option v-for="cat in CATEGORIES" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
      </select>
      <input v-model="form.name" class="pex__input" placeholder="Nombre del gasto (ej. Reparación de portón)" maxlength="120" />
      <label class="pex__field">
        <span class="pex__label">Monto</span>
        <span class="pex__money"><span>$</span><input v-model.number="form.amount" type="number" min="0" step="0.01" class="pex__input pex__input--amount" placeholder="0.00" /></span>
      </label>
      <label class="pex__field">
        <span class="pex__label">Fecha</span>
        <input v-model="form.date" type="date" class="pex__input" />
      </label>
      <p v-if="error" class="pex__error">{{ error }}</p>
      <div class="pex__actions">
        <button type="submit" class="pex__save" :disabled="saving || !form.name || !form.amount">{{ saving ? 'Guardando...' : 'Guardar gasto' }}</button>
        <button type="button" class="pex__cancel" :disabled="saving" @click="adding = false">Cancelar</button>
      </div>
      <p class="pex__hint">Se suma a los gastos del parque en Finanzas y baja el NOI de ese mes. Lo que quieras cobrar a un inquilino se registra desde un ticket.</p>
    </form>
    <p v-if="saved" class="pex__saved">Gasto registrado.</p>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/services/authStore'
import { userHasPageAccess } from '@/constants/accessControl'
import { createExpense, getPlotExpenses, deleteExpense } from '@/services/expenses'

const props = defineProps({ plot: { type: Object, required: true } })

const { state } = useAuthStore()
// El gasto vive en Finanzas: solo quien tiene acceso a esa sección lo ve.
const canUse = computed(() => !!props.plot?.parkId && userHasPageAccess(state.user, 'finanzas'))

const CATEGORIES = [
  { value: 'MAINTENANCE_CORRECTIVE', label: 'Mantenimiento correctivo' },
  { value: 'MAINTENANCE_PREVENTIVE', label: 'Mantenimiento preventivo' },
  { value: 'UTILITIES', label: 'Servicios comunes' },
  { value: 'WATER', label: 'Agua' },
  { value: 'WASTE', label: 'Recolección' },
  { value: 'SECURITY', label: 'Seguridad perimetral' },
  { value: 'ADMIN', label: 'Administración y honorarios' },
  { value: 'OTHER', label: 'Otros' },
]

const summary = ref(null)
const adding = ref(false)
const saving = ref(false)
const saved = ref(false)
const error = ref('')
const today = () => new Date().toISOString().slice(0, 10)
const form = ref({ category: 'MAINTENANCE_CORRECTIVE', name: '', amount: null, date: today() })

async function load() {
  if (!canUse.value) return
  try { summary.value = await getPlotExpenses(props.plot.parkId, props.plot.id) } catch { summary.value = null }
}
watch(() => props.plot?.id, () => { adding.value = false; saved.value = false; load() }, { immediate: true })

const confirmId = ref('')
const deletingId = ref('')
// Primer clic pide confirmación; el segundo elimina el gasto (por si se capturó por error).
async function removeExpense(id) {
  if (confirmId.value !== id) { confirmId.value = id; return }
  deletingId.value = id
  try {
    await deleteExpense(id)
    confirmId.value = ''
    saved.value = false
    await load()
  } catch (e) {
    error.value = e.message
  } finally {
    deletingId.value = ''
  }
}

function startAdding() {
  saved.value = false
  error.value = ''
  form.value = { category: 'MAINTENANCE_CORRECTIVE', name: '', amount: null, date: today() }
  adding.value = true
}

async function save() {
  saving.value = true
  error.value = ''
  try {
    await createExpense(props.plot.parkId, { ...form.value, plotId: props.plot.id })
    adding.value = false
    saved.value = true
    await load()
  } catch (e) {
    error.value = e.message
  } finally {
    saving.value = false
  }
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0)
}
function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}
</script>

<style scoped>
.pex { display: flex; flex-direction: column; gap: 0.5rem; padding-top: 0.75rem; border-top: 1px solid var(--border-active); margin-top: 0.5rem; }
.pex__title { font-size: 0.8rem; color: var(--text-muted); }
.pex__summary { display: flex; align-items: baseline; gap: 0.5rem; }
.pex__total { font-family: var(--font-mono); font-weight: 700; font-size: 0.95rem; color: var(--text-content); }
.pex__sub { font-size: 0.72rem; color: var(--text-muted); }
.pex__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.25rem; }
.pex__list li { display: flex; gap: 0.5rem; font-size: 0.75rem; align-items: baseline; }
.pex__name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-content); }
.pex__date { color: var(--text-muted); white-space: nowrap; }
.pex__amount { font-family: var(--font-mono); color: var(--text-content); white-space: nowrap; }
.pex__del { background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 0.72rem; padding: 0 0.2rem; }
.pex__del:hover { color: #dc2626; }
.pex__del--confirm { color: #dc2626; font-weight: 600; }
.pex__add { align-self: flex-start; background: none; border: 1px dashed var(--border-active); border-radius: 8px; padding: 0.4rem 0.8rem; font-size: 0.78rem; color: #3b82f6; cursor: pointer; }
.pex__add:hover { border-color: #3b82f6; }
.pex__form { display: flex; flex-direction: column; gap: 0.6rem; }
.pex__field { display: flex; flex-direction: column; gap: 0.2rem; }
.pex__label { font-size: 0.72rem; color: var(--text-muted); }
.pex__money { display: flex; align-items: center; gap: 0.25rem; flex: 1; color: var(--text-muted); font-size: 0.8rem; }
.pex__input { width: 100%; box-sizing: border-box; background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.45rem 0.55rem; font-size: 0.82rem; color: #1e293b; font-family: var(--font-body); color-scheme: light; }
.pex__input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
.pex__input--amount { font-family: var(--font-mono); }
.pex__actions { display: flex; gap: 0.5rem; }
.pex__save { background: #3b82f6; color: #fff; border: none; border-radius: 8px; padding: 0.4rem 0.9rem; font-size: 0.8rem; cursor: pointer; }
.pex__save:disabled { opacity: 0.6; cursor: default; }
.pex__cancel { background: none; border: 1px solid var(--border-active); border-radius: 8px; padding: 0.4rem 0.9rem; font-size: 0.8rem; color: var(--text-muted); cursor: pointer; }
.pex__error { margin: 0; font-size: 0.75rem; color: #dc2626; }
.pex__hint { margin: 0; font-size: 0.7rem; color: var(--text-muted); line-height: 1.4; }
.pex__saved { margin: 0; font-size: 0.75rem; color: #16a34a; }
</style>